import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG } from '@/app/core/config/app-config.token';
import { API_BASE_URL } from '@/app/core/http/api-base-url.token';
import { leerCompartido } from '@/app/core/http/lectura-compartida';
import {
  ContextoAcceso,
  IdentidadSinteticaLocal,
  IDENTIDADES_SINTETICAS,
  IDENTIDAD_STORAGE_KEY,
  SuccessEnvelope,
} from '../models';

interface StoredIdentity {
  corporateIdentityId: IdentidadSinteticaLocal['corporateIdentityId'];
  mfaVerificado: boolean;
}

export const IDENTITY_CHANGED_EVENT = 'consola-ventas:identity-changed';

@Injectable({ providedIn: 'root' })
export class AccesoContextoService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly appConfig = inject(APP_CONFIG);

  readonly identidades = IDENTIDADES_SINTETICAS;

  getSelectedIdentity(): IdentidadSinteticaLocal {
    const stored = this.readStoredIdentity();
    return (
      this.identidades.find((identity) => identity.corporateIdentityId === stored?.corporateIdentityId) ??
      this.identidades[0]
    );
  }

  getSelectedMfa(): boolean {
    return this.readStoredIdentity()?.mfaVerificado ?? false;
  }

  setSelectedIdentity(corporateIdentityId: IdentidadSinteticaLocal['corporateIdentityId'], mfaVerificado: boolean): void {
    sessionStorage.setItem(
      IDENTIDAD_STORAGE_KEY,
      JSON.stringify({ corporateIdentityId, mfaVerificado } satisfies StoredIdentity),
    );
    window.dispatchEvent(new CustomEvent(IDENTITY_CHANGED_EVENT));
  }

  async resolveContextoAcceso(
    opciones?: { forzar?: boolean },
  ): Promise<SuccessEnvelope<Omit<ContextoAcceso, 'mfaVerificado' | 'estadoAcceso'>>['data']> {
    const stored = this.readStoredIdentity();
    const clave = `contexto|${stored?.corporateIdentityId ?? 'sin-identidad'}|${stored?.mfaVerificado ?? false}`;

    return leerCompartido(
      this.http,
      clave,
      async () => {
        const envelope = await firstValueFrom(
          this.http.get<SuccessEnvelope<Omit<ContextoAcceso, 'mfaVerificado' | 'estadoAcceso'>>>(
            `${this.apiBaseUrl}/v1/accesos/contexto`,
            { headers: this.buildLocalHeaders() },
          ),
        );

        return envelope.data;
      },
      { ttlMs: 5000, forzar: opciones?.forzar },
    );
  }

  buildLocalHeaders(): HttpHeaders {
    if (this.appConfig.environment !== 'dev' || this.appConfig.auth.modo === 'entra') {
      return new HttpHeaders();
    }

    const storedIdentity = this.readStoredIdentity();
    if (!storedIdentity) {
      return new HttpHeaders();
    }

    return new HttpHeaders({
      'x-corporate-identity-id': storedIdentity.corporateIdentityId,
      'x-mfa-verified': String(storedIdentity.mfaVerificado),
    });
  }

  private readStoredIdentity(): StoredIdentity | null {
    const raw = sessionStorage.getItem(IDENTIDAD_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<StoredIdentity>;
      const corporateIdentityId = parsed.corporateIdentityId;

      if (!this.identidades.some((identity) => identity.corporateIdentityId === corporateIdentityId)) {
        return null;
      }

      return {
        corporateIdentityId: corporateIdentityId as StoredIdentity['corporateIdentityId'],
        mfaVerificado: parsed.mfaVerificado === true,
      };
    } catch {
      return null;
    }
  }
}
